import { Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { PrismaService } from '../prisma/prisma.service';

// Mirrors api's demo-cleanup: a rolling window rather than a synchronized
// wipe, so an active conversation never disappears mid-session — only chat
// sessions that have gone quiet for RETENTION_MINUTES get swept.
const RETENTION_MINUTES = Number(process.env.DEMO_RETENTION_MINUTES ?? 30);

@Injectable()
export class DemoCleanupService {
  private readonly logger = new Logger(DemoCleanupService.name);

  constructor(private readonly prisma: PrismaService) {}

  @Cron('*/5 * * * *')
  async cleanup(): Promise<void> {
    if (process.env.DEMO_MODE !== 'true') return;

    const cutoff = new Date(Date.now() - RETENTION_MINUTES * 60_000);

    const staleSessions = await this.prisma.chatSession.findMany({
      where: { lastMessageAt: { lt: cutoff } },
      select: { id: true },
    });
    if (!staleSessions.length) return;

    const sessionIds = staleSessions.map((s) => s.id);

    // ServiceRequest is a 1:1 wrapper around a session with its own required
    // FKs from events/notes — those must go first or the delete violates them.
    const staleRequests = await this.prisma.serviceRequest.findMany({
      where: { sessionId: { in: sessionIds } },
      select: { id: true },
    });
    if (staleRequests.length) {
      const requestIds = staleRequests.map((r) => r.id);
      await this.prisma.serviceRequestEvent.deleteMany({ where: { serviceRequestId: { in: requestIds } } });
      await this.prisma.serviceRequestNote.deleteMany({ where: { serviceRequestId: { in: requestIds } } });
      await this.prisma.serviceRequest.deleteMany({ where: { id: { in: requestIds } } });
    }

    await this.prisma.chatMessage.deleteMany({ where: { sessionId: { in: sessionIds } } });
    await this.prisma.chatSession.deleteMany({ where: { id: { in: sessionIds } } });

    this.logger.log(
      `Removed ${sessionIds.length} stale demo chat session(s) older than ${RETENTION_MINUTES}m`
    );
  }
}
